import { create } from 'zustand';
import { persist, createJSONStorage, devtools } from 'zustand/middleware';
import { decodeJWT } from '@/utils/jwtUtils';

export interface ModulePermission {
  key: string;
  default_route: string;
}

export interface FunctionalityPermission {
  key: string;
  name: string;
  module_key: string;
  parent_key?: string;
  route?: string;
  show_in_menu: boolean;
  menu_order: number;
}

export interface ComponentPermission {
  key: string;
  component_type: string;
  table_alias?: string;
}

export interface PermissionPayload {
  modules: ModulePermission[];
  functionalities: FunctionalityPermission[];
  routes: string[];
  components: ComponentPermission[];
  table_actions: Record<string, string[]>;
}

interface AuthorizationState {
  permissionToken: string | null;
  payload: PermissionPayload | null;
  selectedModuleKey: string | null;

  setPermissionToken: (token: string | null) => void;
  setSelectedModule: (moduleKey: string | null) => void;
  clearAuthorization: () => void;

  canAccessRoute: (path: string) => boolean;
  canAccessFunctionality: (key: string) => boolean;
  canAccessComponent: (key: string) => boolean;
  getTableActions: (tableAlias: string) => string[];
  getAllowedModules: () => ModulePermission[];
  getFunctionalitiesForModule: (moduleKey: string) => FunctionalityPermission[];
}

const environment = import.meta.env.VITE_APP_ENV;

export const normalizeClientRoutePath = (path?: string | null): string => {
  const trimmed = String(path ?? '').trim();
  if (!trimmed) return '';

  const withLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return withLeadingSlash.replace(/^\/\d+(?=\/)/, '') || '/';
};

export const useAuthorizationStore = create<AuthorizationState>()(
  devtools(
    persist(
      (set, get) => ({
        permissionToken: null,
        payload: null,
        selectedModuleKey: null,

        setPermissionToken: (token) => {
          if (!token) {
            set({ permissionToken: null, payload: null, selectedModuleKey: null });
            return;
          }
          const decoded = decodeJWT(token);
          const payload = decoded?.payload as PermissionPayload | undefined;
          if (!payload) {
            set({ permissionToken: null, payload: null, selectedModuleKey: null });
            return;
          }

          const { selectedModuleKey } = get();
          const modules = payload.modules ?? [];

          let nextModuleKey: string | null = selectedModuleKey;

          if (modules.length === 1) {
            // Single module → auto-select always
            nextModuleKey = modules[0].key;
          } else if (modules.length > 1) {
            // Multi-module: keep current if still valid, else clear (force gateway)
            const stillValid = selectedModuleKey && modules.some((m) => m.key === selectedModuleKey);
            nextModuleKey = stillValid ? selectedModuleKey : null;
          } else {
            nextModuleKey = null;
          }

          set({ permissionToken: token, payload, selectedModuleKey: nextModuleKey });
        },

        setSelectedModule: (moduleKey) => {
          set({ selectedModuleKey: moduleKey });
        },

        clearAuthorization: () => {
          set({ permissionToken: null, payload: null, selectedModuleKey: null });
        },

        canAccessRoute: (path) => {
          const { payload } = get();
          if (!payload) return false;
          const normalizedPath = normalizeClientRoutePath(path);
          return payload.routes.some(
            (route) => normalizeClientRoutePath(route) === normalizedPath,
          );
        },

        canAccessFunctionality: (key) => {
          const { payload } = get();
          if (!payload) return false;
          return payload.functionalities.some((f) => f.key === key);
        },

        canAccessComponent: (key) => {
          const { payload } = get();
          if (!payload) return false;
          return payload.components.some((c) => c.key === key);
        },

        getTableActions: (tableAlias) => {
          const { payload } = get();
          if (!payload) return [];
          return payload.table_actions[tableAlias] ?? [];
        },

        getAllowedModules: () => {
          const { payload } = get();
          return payload?.modules ?? [];
        },

        getFunctionalitiesForModule: (moduleKey) => {
          const { payload } = get();
          if (!payload) return [];
          return payload.functionalities.filter((f) => f.module_key === moduleKey);
        },
      }),
      {
        name: 'authorization-storage',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          permissionToken: state.permissionToken,
          payload: state.payload,
          selectedModuleKey: state.selectedModuleKey,
        }),
      },
    ),
    {
      store: 'authorization',
      name: 'authorization',
      enabled: environment === 'development',
    },
  ),
);
